import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api, type Campaign, type RedeemApp, type Target, type TargetPlatform } from '@/api/client'
import { qk, useCampaigns, useRedeemApps } from '@/lib/queries'
import type { Tone } from '@/lib/status'
import { TONE_CLASS } from '@/lib/status'
import { formatAbsolute } from '@/lib/time'
import { cn } from '@/lib/utils'
import { useToast } from '@/ui'

export const PLATFORM_LABEL: Record<TargetPlatform, string> = {
  threads: 'Threads',
  reddit: 'Reddit (OAuth 앱)',
  reddit_browser: 'Reddit (브라우저 세션)',
  x: 'X (API)',
  instagram: 'Instagram (조회용)',
  bluesky: 'Bluesky (DM 발송)',
}

const DAY_MS = 86_400_000
/** The refresher renews a Threads token from 14 days out; inside 7 the
 *  console warns. */
export const TOKEN_WARN_DAYS = 7

export interface TokenInfo {
  /** Whole days left; null while the refresher has not stored a date. */
  days: number | null
  label: string
  detail: string
  tone?: Tone
}

/** A Threads account's token expiry as D-N. Null for the platforms that have
 *  no expiring token. */
export function tokenInfo(t: Pick<Target, 'platform' | 'token_expires_at'>, now = new Date()): TokenInfo | null {
  if (t.platform !== 'threads') return null
  if (!t.token_expires_at) {
    return { days: null, label: '다음 갱신 때 확인', detail: '만료일은 토큰을 한 번 갱신하면 기록된다' }
  }
  const at = new Date(t.token_expires_at)
  if (Number.isNaN(at.getTime())) return { days: null, label: '다음 갱신 때 확인', detail: '' }
  const days = Math.floor((at.getTime() - now.getTime()) / DAY_MS)
  const detail = `${formatAbsolute(t.token_expires_at, now)} 만료`
  if (days < 0) return { days, label: '만료됨', detail, tone: 'failed' }
  const label = days === 0 ? 'D-day' : `D-${days}`
  if (days < 2) return { days, label, detail, tone: 'failed' }
  if (days <= TOKEN_WARN_DAYS) return { days, label, detail, tone: 'publishing' }
  return { days, label, detail }
}

/** The last connection test: a mark and the platform's message, whole in the
 *  tooltip. */
export function CheckLine({ ok, msg, className }: { ok?: boolean; msg?: string; className?: string }) {
  if (ok === undefined || ok === null) {
    // A replaced credential leaves a note here in place of the old result.
    return <span className={cn('min-w-0 break-words text-muted-foreground', className)}>{msg || '아직 점검하지 않았다'}</span>
  }
  const Icon = ok ? Check : X
  return (
    <span
      title={msg}
      className={cn('inline-flex min-w-0 items-start gap-1', ok ? TONE_CLASS.published.text : TONE_CLASS.failed.text, className)}
    >
      <Icon size={14} className="mt-0.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{msg || (ok ? '정상' : '실패')}</span>
    </span>
  )
}

/** The campaigns and desk apps that post through each account. A campaign
 *  names its account per platform in its pacing; a desk app names one. */
export function useAccountUsage() {
  const campaigns = useCampaigns().data?.items
  const apps = useRedeemApps().data?.items
  return useMemo(() => {
    const byId = (id: number) => ({
      campaigns: (campaigns ?? []).filter((c) =>
        Object.values(c.PlatformConfigs ?? {}).some((p) => p.enabled !== false && p.target_id === id),
      ) as Campaign[],
      apps: (apps ?? []).map((a) => a.app).filter((a) => a.target_id === id) as RedeemApp[],
    })
    return { byId, ready: campaigns !== undefined && apps !== undefined }
  }, [campaigns, apps])
}

/** Connection test. The server records the result on the account, so the
 *  list is read again afterwards. */
export function useTestAccount() {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: (id: number) => api.testTarget(id),
    onSuccess: (r) => {
      if (r.supported === false) toast.info(r.msg)
      else if (r.ok) toast.success(`연결됨 — ${r.msg}`)
      else toast.error(`연결 실패 — ${r.msg}`)
    },
    onError: (e) => toast.error(`점검하지 못했다: ${e instanceof Error ? e.message : String(e)}`),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.targets }),
  })
}

export function useInvalidateAccounts() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: qk.targets })
}

/** The first line of a persona, for a card. */
export function voiceLine(t: Pick<Target, 'voice'>): string {
  return (t.voice?.persona ?? '').split('\n').find((l) => l.trim())?.trim() ?? ''
}
